import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowRight } from "@phosphor-icons/react/ArrowRight";
import RouterLink from "next/link";
import * as React from "react";

import { headingFontFamily } from "./heading-font";

export interface StoryCardProps {
	image: string;
	title: string;
	description: string;
	href: string;
	ctaLabel: string;
	/** Chip label; the chip is omitted entirely when absent. */
	eyebrow?: string;
	/** Optional chip icon. */
	icon?: React.ReactElement;
	/**
	 * When true the whole card is the link and the CTA renders as a visual
	 * (non-interactive) button. When false the CTA button is itself the link.
	 */
	clickableCard?: boolean;
}

/**
 * The image-led story card used by the home "use cases" section and the
 * case-studies index. Hover lifts the card, zooms the image, and slides the
 * CTA arrow in.
 */
export function StoryCard({
	image,
	title,
	description,
	href,
	ctaLabel,
	eyebrow,
	icon,
	clickableCard = false,
}: StoryCardProps) {
	const [hovered, setHovered] = React.useState(false);

	const media = (
		<Box
			sx={{
				height: { xs: "220px", md: "240px" },
				overflow: "hidden",
				position: "relative",
			}}>
			<CardMedia
				alt={description}
				component="img"
				image={image}
				sx={{
					height: "100%",
					width: "100%",
					objectFit: "cover",
					transition: "transform 0.6s ease",
					transform: hovered ? "scale(1.24)" : "scale(1.2)",
				}}
			/>
		</Box>
	);

	const content = (
		<CardContent
			sx={{
				flexGrow: 1,
				pb: 2,
				pt: { xs: 3, md: 4 },
				px: { xs: 3, md: 4 },
			}}>
			<Stack spacing={2}>
				{eyebrow ? (
					<div>
						<Chip
							color="secondary"
							icon={icon}
							label={eyebrow}
							size="small"
							variant="soft"
						/>
					</div>
				) : null}
				<Typography
					component="div"
					sx={{ fontFamily: headingFontFamily }}
					variant="h5">
					{title}
				</Typography>
				<Typography
					sx={{ color: "text.secondary" }}
					variant="body1">
					{description}
				</Typography>
			</Stack>
		</CardContent>
	);

	const ctaArrow = (
		<Box
			sx={{
				display: "inline-flex",
				opacity: hovered ? 1 : 0,
				transform: hovered ? "translateX(0)" : "translateX(-10px)",
				transition: "transform 0.3s, opacity 0.3s",
			}}>
			<ArrowRight size={16} />
		</Box>
	);

	const actions = (
		<CardActions
			sx={{
				pb: { xs: 3, md: 4 },
				pt: 0,
				px: { xs: 3, md: 4 },
			}}>
			{clickableCard ? (
				<Button
					color="secondary"
					component="span"
					endIcon={ctaArrow}
					size="large"
					variant="text">
					{ctaLabel}
				</Button>
			) : (
				<Button
					color="secondary"
					component={RouterLink}
					endIcon={ctaArrow}
					href={href}
					size="large"
					variant="text">
					{ctaLabel}
				</Button>
			)}
		</CardActions>
	);

	return (
		<Card
			elevation={hovered ? 8 : 6}
			onMouseEnter={() => {
				setHovered(true);
			}}
			onMouseLeave={() => {
				setHovered(false);
			}}
			sx={{
				borderRadius: 4,
				display: "flex",
				flexDirection: "column",
				height: "100%",
				alignItems: "stretch",
				overflow: "hidden",
				transition: "transform 0.3s, box-shadow 0.3s",
			}}>
			{clickableCard ? (
				<CardActionArea
					component={RouterLink}
					href={href}
					sx={{
						height: "100%",
						display: "flex",
						flexDirection: "column",
						alignItems: "stretch",
					}}>
					{media}
					{content}
					{actions}
				</CardActionArea>
			) : (
				<>
					{media}
					{content}
					{actions}
				</>
			)}
		</Card>
	);
}
